import Head from 'next/head';
import React, { memo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { BlockFeatured } from '../../../../graphdebate/src/components/Layout/BlockFeatured';
import { BlockLastTopics } from '../../../../graphdebate/src/components/Layout/BlockLastTopics';
import { BlockSidebarFeatured } from '../../../../graphdebate/src/components/Layout/BlockSidebarFeatured';
import { BlockPressFeed } from '../../../../graphdebate/src/components/Layout/press/BlockPressFeed';
import { BlockPressHeadline } from '../../../../graphdebate/src/components/Layout/press/BlockPressHeadline';
import { BlockPressRest } from '../../../../graphdebate/src/components/Layout/press/BlockPressRest';
import { BlockReadersCircle } from '../../../../graphdebate/src/components/Layout/press/BlockReadersCircle';
import { ReadersForumBlock } from '../../../../graphdebate/src/components/Layout/ReadersForumBlock';
import { useWebsite } from '../../hooks/useWebsite';
import type { LayoutBlock } from '../../types/layout';
import { html } from '../../utils/dom';
import { BlockArticle } from './BlockArticle';
import { handleLayoutBlockDisplayClass } from './blockCheck';

// Magazine press blocks are gated by the media mode: they never render in forum mode,
// even if configured in the BO (ticket B2_02 — gate dur, decision Mathias 2026-06-23).
// 'readers-forum' (B4_03) is community-facing but shares the same media gate (offered
// only in media mode in the BO, so it must never render in forum mode either).
const PRESS_BLOCK_TYPES = [
  'press-headline',
  'press-feed',
  'press-rest',
  'readers-circle',
  'readers-forum',
];

interface BlockProps {
  block: LayoutBlock;
  stickyOffset?: number;
}

export const Block: React.FC<BlockProps> = memo(({ block, stickyOffset }) => {
  const blockRef = useRef<HTMLDivElement | null>(null);
  const { graphdebate } = useWebsite();
  const { type, content, locate, sticky: isSticky } = block;

  // Gate dur : un bloc presse ne rend rien hors mode média.
  if (PRESS_BLOCK_TYPES.includes(type) && graphdebate?.mode !== 'media') {
    return null;
  }

  // Le menu de rubriques (headings-nav) est intercepté par MainLayout et rendu sous la
  // navbar, jamais via le système d'aires — garde contre un rendu accidentel ici.
  if (type === 'headings-nav') {
    return null;
  }

  // Page statique (type 'static-page', D32) : le bloc EST une page autonome, rendue par
  // StaticPageView via le fallback de la route [tag] — jamais comme bloc de zone. Ceinture :
  // il est aussi exclu du groupByBlockLocation dans MainLayout (comme headings-nav).
  if (type === 'static-page') {
    return null;
  }

  let element = <></>;

  if (!content || typeof content !== 'string') {
    /* if (block.type === 'look-a-like-from-external-url' && BlockExternal) {
      element = BlockExternal;
    } else */ if (type === 'similar-articles') {
      element = <BlockArticle block={block} />;
    } else if (type === 'last-topics' && locate === 'right') {
      element = (
        <BlockLastTopics
          className="pb-2 lg:[&_.CreateTopicButton_button>div]:gap-x-1"
          block={block}
          itemTitleClassName="md:text-base md:font-normal md:leading-5"
          withLinkToAllCTA
          isResponsive={false}
          isSidebar={true}
        />
      );
    } else if (type === 'last-topics') {
      element = (
        <BlockLastTopics className="lg:px-4 pb-2 max-w-5xl m-auto" block={block} withLinkToAllCTA />
      );
    } else if (type === 'featured') {
      element = (
        <BlockFeatured className="px-4 pb-2 max-w-5xl m-auto" block={block} withLinkToAllCTA />
      );
    } else if (type === 'sidebar-featured') {
      element = <BlockSidebarFeatured className="px-4 pb-2" block={block} />;
    } else if (type === 'press-headline') {
      element = <BlockPressHeadline block={block} />;
    } else if (type === 'press-feed') {
      element = <BlockPressFeed block={block} />;
    } else if (type === 'press-rest') {
      element = <BlockPressRest block={block} />;
    } else if (type === 'readers-circle') {
      element = <BlockReadersCircle block={block} />;
    } else if (type === 'readers-forum') {
      element = <ReadersForumBlock block={block} />;
    } else {
      element = <>{content}</>;
    }
  }

  const blockCss =
    type !== 'sidebar-featured' && // TODO: fix for now. Waiting BO update to remove it
    (block.css_prefixed || '')
      .replace(/ :scope/g, '') // allow passing ":scope" to reference block container
      .replace(/(\r\n|\n|\r)/gm, ''); // remove line breaks

  const blockContentJsx = content && typeof content === 'string' ? html(content) : element;

  return (
    <>
      {!!blockContentJsx && (
        <div
          id={`block-${block._id}`}
          ref={blockRef}
          className={twMerge(
            'layout-block',
            `locate-${locate}`,
            ['left', 'right'].includes(locate) && 'locate-sidebar',
            handleLayoutBlockDisplayClass(block),
            isSticky ? 'md:sticky bg-white md:z-[10]' : 'overflow-hidden',
          )}
          {...(isSticky && { style: { top: `${stickyOffset || 0}px` } })}
        >
          {blockContentJsx}
        </div>
      )}
      {blockCss ? (
        <Head>
          <style>{blockCss}</style>
        </Head>
      ) : null}
    </>
  );
});
